import { useEffect, useMemo, useState } from "react";
import Form from "V2.0/components/Form";
import {
  actionNameField,
  assistantField,
  commonSchema,
  descriptionFieldRequiredSchema,
  fromPhoneNumberField,
  messageTemplateField,
  phoneNumberField,
  transferConversationMemoryField,
  triggerDescriptionField,
  triggerEventField,
} from "./models/fields";
import { processTemplateForParameters as processMetaTemplateForParameters } from "V2.0/HomePages/HomeCampaign/SingleContact/WhatsAppV2/Meta/utils";
import { Button } from "V2.0/common/button";
import { PlusIcon } from "@heroicons/react/24/outline";
import WhatsappTemplateAttributesAdd from "V2.0/HomePages/HomeCampaign/SingleContact/WhatsAppV2/WhatsappTemplateAttributesAdd";

function initializeWhatsappTrigger(whatsappTrigger) {
  return {
    actionName: whatsappTrigger?.actionName || "",
    phoneNumber: whatsappTrigger?.phoneNumber || "",
    trigger: whatsappTrigger?.trigger || "",
    assistant: whatsappTrigger?.whatsAppTemplateDetails?.assistant || null,
    fromNumber: whatsappTrigger?.fromNumber || null,
    template: JSON.stringify({
      templateName:
        whatsappTrigger?.whatsAppTemplateDetails?.templateName || "",
      templateLanguage:
        whatsappTrigger?.whatsAppTemplateDetails?.templateLanguage || "",
    }),
    transferConversationMemory:
      whatsappTrigger?.whatsAppTemplateDetails?.transferConversationMemory ||
      false,
    triggerEvent: whatsappTrigger?.triggerEvent || "duringCall",
  };
}

export default function WhatsappTriggerForm(props) {
  const {
    whatsappTrigger,
    assistants,
    whatsappTemplates,
    onCancel,
    onSubmit,
    whatsappConfiguration,
    mcubeEngageTemplates,
    mcubeEngageConfiguration,
  } = props;

  const [inputField] = useState(() =>
    initializeWhatsappTrigger(whatsappTrigger),
  );

  const [activeTemplate, setActiveTemplate] = useState(null);
  const [activeProvider, setActiveProvider] = useState(null);
  const [showAttributesAdd, setShowAttributesAdd] = useState(false);
  const [showTwilioAttributesAdd, setShowTwilioAttributesAdd] = useState(false);
  const [attributes, setAttributes] = useState(
    whatsappTrigger?.whatsAppTemplateDetails?.templateInputs || {},
  );
  const [previewData, setPreviewData] = useState(inputField);
  const [loading, setLoading] = useState(false);
  const [errorMessage] = useState({});

  const parameters = useMemo(() => {
    if (!Boolean(activeTemplate) || !Boolean(activeProvider)) return [];

    const processTemplateParameters = {
      META: processMetaTemplateForParameters,
      "MCUBE ENGAGE": processMetaTemplateForParameters,
    };

    const processedParameters = (
      processTemplateParameters[activeProvider] ??
      function () {
        return [];
      }
    )(activeTemplate);

    return processedParameters;
  }, [activeTemplate, activeProvider]);

  useEffect(() => {
    const inputFieldTemplate = JSON.parse(previewData?.template);

    if (!activeProvider || !Boolean(inputFieldTemplate?.templateName)) {
      setActiveTemplate(null);
      return;
    }

    const templateName = inputFieldTemplate.templateName;

    const [templates, processTemplate] = (() => {
      switch (activeProvider) {
        case "META":
          return [whatsappTemplates, (template) => template];
        case "MCUBE ENGAGE":
          return [
            mcubeEngageTemplates,
            (template) => {
              return {
                ...template,
                components: JSON.parse(template.components),
              };
            },
          ];
        default:
          return [[], () => {}];
      }
    })();

    const template = templates.find((_messageTemplate) => {
      return _messageTemplate.name === templateName;
    });

    if (template) {
      const processedTemplate = processTemplate(template);
      setActiveTemplate(processedTemplate);
      return;
    }

    setActiveTemplate(null);
  }, [previewData]);

  const updatedAssistantField = useMemo(() => {
    if (assistants?.length > 0) {
      const whatsappAssistants = assistants.filter(
        (assistant) => assistant.assistantType === "whatsapp",
      );

      if (whatsappAssistants.length > 0) {
        const { name, label, type, options } = assistantField;
        const whatsappAssistantOptions = whatsappAssistants.map(
          (assistant, idx) => {
            return {
              id: idx + 1,
              value: assistant._id,
              label: assistant.name,
            };
          },
        );

        return {
          name,
          label,
          type,
          options: [...options, ...whatsappAssistantOptions],
        };
      }
    }

    return assistantField;
  }, [assistants]);

  const updatedFromNumberField = useMemo(() => {
    if (previewData.assistant) {
      const { name, type, label } = fromPhoneNumberField;
      const assistant = assistants.find(
        (assistant) => assistant._id === previewData.assistant,
      );

      if (!assistant) {
        return fromPhoneNumberField;
      }

      const assistantPhoneNumberAsOptions =
        assistant?.communicationBinding?.map((binding, idx) => {
          return {
            id: idx + 1,
            label: binding.identifier,
            value: binding.identifier,
          };
        });

      return {
        name,
        type,
        label,
        options: [
          { id: 0, label: "Please select phone number", value: "" },
          ...assistantPhoneNumberAsOptions,
        ],
      };
    }

    return fromPhoneNumberField;
  }, [previewData, assistants]);

  const updatedMessageTemplateField = useMemo(() => {
    if (!Boolean(previewData.fromNumber)) {
      return messageTemplateField;
    }

    if (whatsappConfiguration.isLoading || mcubeEngageConfiguration.isLoading) {
      return messageTemplateField;
    }

    const { templates, getTemplateOption, updateActiveProvider } = (() => {
      if (
        whatsappConfiguration.isConfigured &&
        whatsappConfiguration?.data?.phoneNumberIds?.includes(
          previewData?.fromNumber,
        ) &&
        whatsappTemplates.length > 0
      ) {
        return {
          templates: whatsappTemplates,
          getTemplateOption: (template, id) => {
            return {
              id,
              value: JSON.stringify({
                templateName: template.name,
                templateLanguage: template.language,
              }),
              label: template.name,
            };
          },

          updateActiveProvider: () => setActiveProvider("META"),
        };
      }

      if (
        mcubeEngageConfiguration.isConfigured &&
        mcubeEngageConfiguration?.data?.phoneNumbers.includes(
          previewData?.fromNumber,
        ) &&
        mcubeEngageTemplates.length > 0
      ) {
        return {
          templates: mcubeEngageTemplates,
          getTemplateOption: (template, id) => {
            return {
              id,
              value: JSON.stringify({
                templateName: template.name,
                templateLanguage: template.language,
              }),
              label: template.name,
            };
          },
          updateActiveProvider: () => setActiveProvider("MCUBE ENGAGE"),
        };
      }
    })();

    const { name, type, label, options } = messageTemplateField;
    const templateFieldOptions = templates.map((template, idx) =>
      getTemplateOption(template, idx + 1),
    );

    const templateMessageFieldWithTemplateOptions = {
      name,
      type,
      label,
      options: [...options, ...templateFieldOptions],
    };

    updateActiveProvider();

    return templateMessageFieldWithTemplateOptions;
  }, [
    previewData,
    whatsappTemplates,
    mcubeEngageTemplates,
    whatsappConfiguration,
    mcubeEngageConfiguration,
  ]);

  const templateFieldWithInfo = useMemo(() => {
    if (!Boolean(activeTemplate)) return updatedMessageTemplateField;

    const [onViewAttributes] = (() => {
      switch (activeProvider) {
        case "META":
          return [() => setShowAttributesAdd(true)];
        case "MCUBE ENGAGE":
          return [() => setShowAttributesAdd(true)];
        default:
          return [() => {}];
      }
    })();

    const infoLabel =
      parameters.length > 0 ? (
        <>
          <PlusIcon /> Attributes
        </>
      ) : (
        "View Message"
      );

    return {
      ...updatedMessageTemplateField,
      info: (
        <Button
          plain
          onClick={onViewAttributes}
        >
          {infoLabel}
        </Button>
      ),
    };
  }, [parameters, activeTemplate, activeProvider, updatedMessageTemplateField]);

  const { template, schema } = useMemo(() => {
    const template = [
      actionNameField,
      phoneNumberField,
      triggerEventField,
      previewData.triggerEvent === "duringCall" && triggerDescriptionField,
      updatedAssistantField,
      updatedFromNumberField,
      templateFieldWithInfo,
      transferConversationMemoryField,
    ];

    const schema =
      previewData.triggerEvent === "duringCall"
        ? descriptionFieldRequiredSchema
        : commonSchema;

    return {
      template,
      schema,
    };
  }, [
    previewData,
    updatedAssistantField,
    updatedFromNumberField,
    templateFieldWithInfo,
  ]);

  const addTriggerButtonInfo = {
    buttons: "2",
    primaryLabel: "Save",
    secondaryLabel: "Cancel",
  };

  const handleYes = (data) => {
    const lifecycleCbs = {
      init: () => {
        setLoading(true);
      },
      success: () => {
        setLoading(false);
        onCancel();
      },
      error: (e) => {
        setLoading(false);
      },
    };

    const { template, transferConversationMemory, assistant, ...restData } =
      data;
    const { templateName, templateLanguage } = JSON.parse(template);

    const payload = {
      ...restData,
      whatsAppTemplateDetails: {
        templateName,
        templateLanguage,
        templateInputs: attributes,
        transferConversationMemory: Boolean(assistant)
          ? transferConversationMemory
          : false,
        assistant,
      },
      actionFunction: "WHATSAPP",
    };
    setLoading(false);
    onSubmit(payload, lifecycleCbs);
  };

  const getPreviewDataInParent = (dataDuplicate) => {
    if (!dataDuplicate) {
      setPreviewData(inputField);
    }
    setPreviewData((prevData) => {
      if (prevData.fromNumber != dataDuplicate.fromNumber) {
        setActiveProvider(null);
        setAttributes({});
        setActiveTemplate(null);
        return {
          ...dataDuplicate,
          template: JSON.stringify({
            templateName: "",
            templateLanguage: "",
          }),
        };
      } else {
        return dataDuplicate;
      }
    });
  };

  return (
    <>
      <Form
        key={JSON.stringify(inputField)}
        errorMessage={errorMessage}
        inputField={inputField}
        template={template}
        schema={schema}
        buttonInfo={addTriggerButtonInfo}
        onSubmit={handleYes}
        handleSecondary={onCancel}
        loading={loading}
        setLoading={setLoading}
        getValue={getPreviewDataInParent}
      />
      {showAttributesAdd && (
        <WhatsappTemplateAttributesAdd
          parametersInitialValues={attributes}
          template={activeTemplate}
          onUpdateParameters={(data) => setAttributes(data)}
          onClose={() => setShowAttributesAdd(false)}
        />
      )}
    </>
  );
}
